import { useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle, PaperPlaneTilt } from "@phosphor-icons/react";

const TOPICS = ["Order question", "Product question", "Shipping", "Other"];
const EMPTY = { name: "", email: "", topic: "", order_ref: "", message: "", website: "" };
const field = "w-full bg-white/5 border border-white/20 px-4 py-3 text-sm focus:border-primary outline-none transition-colors";

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="label-mono text-muted-foreground text-[0.6rem] block mb-2">{children}</label>
);

export const ContactForm = () => {
  const [f, setF] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.topic) { setError("Please pick a topic."); return; }
    setSending(true); setError("");
    try {
      await api.post("/contact", f);
      setSent(true); setF(EMPTY);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Please check your name, email and message (at least 5 characters).");
    } finally { setSending(false); }
  };

  if (sent) {
    return (
      <div data-testid="contact-success" className="mt-12 border border-primary/40 bg-primary/5 p-8">
        <CheckCircle size={28} className="text-primary mb-3" />
        <h2 className="font-head text-2xl mb-2">Thanks — your message is in.</h2>
        <p className="text-muted-foreground text-sm">We read every message and will reply to your email as soon as we can.</p>
        <button data-testid="contact-send-another" onClick={() => setSent(false)} className="mt-5 label-mono text-primary hover:underline text-[0.65rem]">Send another message</button>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} className="mt-12 border border-white/10 bg-white/[0.02] p-6 md:p-8 space-y-5">
      <div>
        <h2 className="font-head text-2xl">Send us a message</h2>
        <p className="text-muted-foreground text-sm mt-1">We'll reply to the email you enter below.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div><Label htmlFor="c-name">Name</Label><input id="c-name" data-testid="contact-name" required maxLength={100} value={f.name} onChange={set("name")} className={field} /></div>
        <div><Label htmlFor="c-email">Email</Label><input id="c-email" data-testid="contact-email" type="email" required value={f.email} onChange={set("email")} className={field} /></div>
      </div>
      <div>
        <Label>Topic</Label>
        <div className="flex flex-wrap gap-2">
          {TOPICS.map((t) => (
            <button type="button" key={t} data-testid={`contact-topic-${t.split(" ")[0].toLowerCase()}`} onClick={() => { setF({ ...f, topic: t }); setError(""); }}
              className={`label-mono text-[0.6rem] px-3 py-2 border transition-colors ${f.topic === t ? "border-primary text-primary bg-primary/10" : "border-white/20 text-muted-foreground hover:border-white/40"}`}
              style={{ transition: "color 0.2s, border-color 0.2s, background-color 0.2s" }}>{t}</button>
          ))}
        </div>
      </div>
      <div><Label htmlFor="c-order">Order # (optional)</Label><input id="c-order" data-testid="contact-order-ref" maxLength={40} placeholder="MPC-XXXXXXXX" value={f.order_ref} onChange={set("order_ref")} className={`${field} sm:w-64 font-mono`} /></div>
      <div><Label htmlFor="c-msg">Message</Label><textarea id="c-msg" data-testid="contact-message" required minLength={5} maxLength={3000} rows={5} value={f.message} onChange={set("message")} className={`${field} resize-y`} /></div>
      <input tabIndex={-1} autoComplete="off" aria-hidden="true" value={f.website} onChange={set("website")} className="hidden" name="website" />
      {error && <p data-testid="contact-error" className="text-destructive text-sm">{error}</p>}
      <button data-testid="contact-submit" type="submit" disabled={sending}
        className="label-mono px-6 py-3 bg-primary text-black hover:bg-[#E6C766] transition-colors inline-flex items-center gap-2 disabled:opacity-50">
        <PaperPlaneTilt size={14} /> {sending ? "Sending…" : "Send message"}
      </button>
    </form>
  );
};
